import isWebWorker from 'detect-webworker'
import isNode from 'detect-node'
import createWorker from './create-worker'

let worker = null
let id = 0

/**
 * Add entropy in order to seed the RNG
 *
 * @return {Promise}
 * @private
 */
function addEntropy () {
  const crypto = window.crypto || window.msCrypto
  if (!crypto) return Promise.reject(new Error('No crypto in browser'))
  const ab = new Uint32Array(1024 / 32)
  crypto.getRandomValues(ab)
  return run(worker, 'addEntropy', [ab.buffer, 1024, 'crypto.getRandomValues'], [ab.buffer])
}

function runWrapper (operation, args, transferables) {
  if (isWebWorker || isNode) return Promise.reject(new Error('can be called in browser environment only'))
  return Promise.resolve()
  .then(() => !worker && init())
  .then(() => run(worker, operation, args, transferables))
}

/**
 * Run operation in web worker
 *
 * @param {WebWorker} worker
 * @param {String} operation name of the operation
 * @param {Array} args arguments which will be applied to the operation
 * @param {Array} [transferables] transferables objects which will be copied efficiently through transferring ownership
 * @return {Promise} the returned value from the operation
 * @private
 */
function run (worker, operation, args, transferables) {
  return new Promise((resolve, reject) => {
    const currentId = ++id
    function message ({ data: { id: _id, result, err } }) {
      if (_id !== currentId) return
      worker.removeEventListener('message', message)
      if (err) return reject(err)
      resolve(result)
    }
    worker.addEventListener('message', message)
    if (transferables && transferables.length) worker.postMessage({ id: currentId, operation, args }, transferables)
    else worker.postMessage({ id: currentId, operation, args }) // firefox throws error when empty array passed
  })
}

export function init () {
  return createWorker()
  .then((_worker) => {
    worker = _worker
    return addEntropy()
  })
}

export function deriveKey (password, params) {
  return runWrapper('deriveKey', [password, params])
}

export function createRandom (bits) {
  return runWrapper('createRandom', [bits])
}

export function createKey () {
  return runWrapper('createKey')
}

export function createIV () {
  return runWrapper('createIV')
}

export function createAsymmetricKeys (privateKey) {
  return runWrapper('createAsymmetricKeys', [privateKey])
}

export function createAsymmetricKeysECDSA (privateKey) {
  return runWrapper('createAsymmetricKeysECDSA', [privateKey])
}

export function signWithECDSAPrivateKey (privateKey, text) {
  return runWrapper('signWithECDSAPrivateKey', [privateKey, text])
}

export function verifyWithECDSAPublicKey (publicKey, text, signature) {
  return runWrapper('verifyWithECDSAPublicKey', [publicKey, text, signature])
}

export function encryptText (data, key, encryptionParams) {
  return runWrapper('encryptText', [data, key, encryptionParams])
}

export function decryptText (data, key, encryptionParams) {
  return runWrapper('decryptText', [data, key, encryptionParams])
}

export function encryptObject (data, key, encryptionParams) {
  return runWrapper('encryptObject', [data, key, encryptionParams])
}

export function decryptObject (data, key, encryptionParams) {
  return runWrapper('decryptObject', [data, key, encryptionParams])
}

export function encryptKey (data, key, encryptionParams) {
  return runWrapper('encryptKey', [data, key, encryptionParams])
}

export function decryptKey (data, key, encryptionParams) {
  return runWrapper('decryptKey', [data, key, encryptionParams])
}

export function encryptRecord (headerKey, metadata, blocks) {
  return runWrapper(
    'encryptRecord',
    [headerKey, metadata, blocks],
    blocks && blocks.map((block) => block.files).reduce((a, b) => a.concat(b), []).map((file) => file.data)
  )
}

export function decryptRecord (record, headerKey) {
  return runWrapper(
    'decryptRecord',
    [record, headerKey],
    record && record.blocks && record.blocks.map((block) => block.files).reduce((a, b) => a.concat(b), []).map((file) => file.data).filter((data) => data)
  )
}

export function decryptKeyWithSymmetricEncryption (password, header) {
  return runWrapper('decryptKeyWithSymmetricEncryption', [password, header])
}

export function decryptKeyWithAsymmetricEncryption (id, version, privateKey, header) {
  return runWrapper('decryptKeyWithAsymmetricEncryption', [id, version, privateKey, header])
}

export function decryptData (data, key, encryptionParams) {
  return runWrapper('decryptData', [data, key, encryptionParams])
}

export function decryptFile (file, key, encryptionParams) {
  return runWrapper('decryptFile', [file, key, encryptionParams], file && [file])
}
